import React from "react";
import MoneyInput from "@/components/MoneyInput";
import InfoTip from "@/components/onboarding/InfoTip";
import { monthNames, FREQ_LABELS, contingencyAmount } from "@/lib/costHelpers";
import { formatGBP } from "@/lib/brand";

const MonthGrid = ({ c, i, startIso, onPatch }) => {
  const labels = monthNames(startIso);
  const active = c.months_active || [];
  const setActive = (list) => onPatch({ months_active: [...new Set(list)].sort((a, b) => a - b) });
  const setAmount = (k, n) => {
    const arr = Array.isArray(c.monthly_amounts) && c.monthly_amounts.length === 12 ? [...c.monthly_amounts] : Array(12).fill(null);
    arr[k] = n;
    onPatch({ monthly_amounts: arr });
  };
  return (
    <div className="mt-4" data-testid={`recurring-custom-grid-${i}`}>
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <label className="text-xs font-semibold">Tick the months it leaves your account, then adjust any amounts that differ</label>
        <button type="button" className="text-xs underline font-semibold" onClick={() => setActive([...Array(12).keys()])} data-testid={`recurring-tick-all-${i}`}>
          Tick all 12
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {labels.map((m, k) => {
          const on = active.includes(k);
          return (
            <div key={k} className={`rounded-lg border-[1.5px] border-black p-2 ${on ? "bg-[#FFF7E5]" : "bg-white"}`}>
              <button
                type="button"
                onClick={() => setActive(on ? active.filter((x) => x !== k) : [...active, k])}
                data-testid={`recurring-month-${i}-${k}`}
                className={`w-full px-2 py-1 rounded-md text-sm font-heading uppercase tracking-wide ${on ? "bg-[#FFBD4A]" : "bg-white border border-black/30"}`}
              >
                {m}
              </button>
              {on && (
                <div className="mt-2">
                  <MoneyInput
                    value={c.monthly_amounts?.[k] ?? c.amount}
                    onChange={(n) => setAmount(k, n)}
                    testid={`recurring-month-amount-${i}-${k}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const RegularCostFields = ({ c, i, startIso, onPatch }) => {
  const freq = c.frequency || "monthly";
  const labels = monthNames(startIso, true);
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold mb-1">What is it?</label>
          <input className="input" data-testid={`recurring-name-${i}`} value={c.name} onChange={(e) => onPatch({ name: e.target.value })} placeholder="e.g. Canva subscription" maxLength={120} />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">
            Regular amount (£)
            <InfoTip testid={`recurring-amount-tip-${i}`}>
              Your regular recurring amount, as it leaves your bank (including VAT). If it varies, choose
              'Custom months & amounts' under How often?
            </InfoTip>
          </label>
          <MoneyInput value={c.amount} onChange={(n) => onPatch({ amount: n })} testid={`recurring-amount-${i}`} placeholder="120" />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="block text-xs font-semibold mb-1">
            How often?
            <InfoTip testid={`recurring-freq-tip-${i}`}>Only add a regular cost if it's due in the next 12 months.</InfoTip>
          </label>
          <select className="input" value={freq} onChange={(e) => onPatch({ frequency: e.target.value })} data-testid={`recurring-freq-${i}`}>
            {Object.entries(FREQ_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        {freq !== "monthly" && freq !== "custom" && (
          <div>
            <label className="block text-xs font-semibold mb-1">{freq === "annual" ? "Which month?" : "First time it lands"}</label>
            <select className="input" value={c.anchor_month_index || 0} onChange={(e) => onPatch({ anchor_month_index: Number(e.target.value) })} data-testid={`recurring-anchor-${i}`}>
              {labels.map((m, k) => (
                <option key={k} value={k}>{m}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {freq === "custom" && <MonthGrid c={c} i={i} startIso={startIso} onPatch={onPatch} />}
    </div>
  );
};

export const ContingencyFields = ({ c, costs, onPatch }) => (
  <div className="space-y-3" data-testid="contingency-fields">
    <p className="text-sm text-black/75">
      A small monthly buffer for the unexpected.
      <InfoTip testid="contingency-tip">
        A safety net for surprise costs, like a broken laptop, a price rise or a forgotten renewal. Your AI
        coach treats it as a buffer, not as spending you need to hit.
      </InfoTip>
    </p>
    <ContingencyChoice mode={c.contingency_mode} pct={c.contingency_pct} amount={c.amount} preview={contingencyAmount(costs, { ...c, contingency_mode: "pct" })} onChange={onPatch} />
  </div>
);

export const ContingencyChoice = ({ mode = "fixed", pct, amount, preview, onChange }) => (
  <div className="space-y-3">
    <label className="flex items-center gap-3 text-sm">
      <input type="radio" checked={mode !== "pct"} onChange={() => onChange({ contingency_mode: "fixed" })} data-testid="contingency-mode-fixed" />
      <span className="shrink-0">A fixed amount a month</span>
      {mode !== "pct" && (
        <span className="w-36"><MoneyInput value={amount} onChange={(n) => onChange({ amount: n })} placeholder="e.g. 100" testid="contingency-amount" /></span>
      )}
    </label>
    <label className="flex items-center gap-3 text-sm flex-wrap">
      <input type="radio" checked={mode === "pct"} onChange={() => onChange({ contingency_mode: "pct" })} data-testid="contingency-mode-pct" />
      <span className="shrink-0">A percentage of my regular costs</span>
      {mode === "pct" && (
        <>
          <span className="w-24 relative">
            <input
              className="input pr-7"
              inputMode="numeric"
              value={pct ?? ""}
              placeholder="e.g. 10"
              onChange={(e) => onChange({ contingency_pct: e.target.value.replace(/[^\d.]/g, "").slice(0, 5) })}
              data-testid="contingency-pct"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-black/55 text-sm">%</span>
          </span>
          <span className="text-black/70" data-testid="contingency-preview">about <strong>{formatGBP(preview)}</strong> a month</span>
        </>
      )}
    </label>
  </div>
);
